/**
 * ============================================================================
 * AJAX CART - KAMPAGNEN & GESCHENKE VERWALTUNG
 * ============================================================================
 *
 * Dieses Skript verwaltet Kampagnen und automatische Geschenke im Warenkorb.
 * Es nutzt Liquid Ajax Cart Mutations, um den Warenkorb dynamisch zu aktualisieren.
 *
 * WICHTIG:
 * Das Script fügt nur Produkte hinzu oder löscht - Shopify zieht selber Rabatte ab!
 * Shopify Discount-Regeln bestimmen ob ein Rabatt abgezogen wird
 */

(function () {

  // ============================================================================
  // KONSTANTEN & INITIALISIERUNG
  // ============================================================================

  /**
   * Schlüssel für den Local Storage, unter dem die aktive Kampagne gespeichert wird
   * @constant {string}
   */
  const STORAGE_KEY = 'active_campaign';

  /**
   * Kampagnen-Daten aus dem JSON-Element im DOM lesen
   * Die Daten werden aus dem Element mit der ID 'ds-cart-campaigns' gelesen
   * und sind base64-kodiert
   */
  const campaignsEl = document.getElementById('ds-cart-campaigns');
  if (!campaignsEl) return;
  const campaignsJson = JSON.parse(campaignsEl.textContent);
  const existingCampaigns = JSON.parse(new TextDecoder().decode(Uint8Array.from(atob(campaignsJson.campaigns), c => c.charCodeAt(0))));


  // ============================================================================
  // HILFSFUNKTIONEN - KAMPAGNEN VERWALTUNG
  // ============================================================================

  /**
   * Bestimmt die aktuell aktive Kampagne basierend auf dem Warenkorb-Status
   *
   * Priorität:
   * 1. Kampagne, deren Code im cart.attributes.active_code gesetzt ist
   * 2. Kampagne mit anwendbarem Discount-Code (applicable === true)
   * 3. Kampagne, deren Code im Warenkorb liegt (auch wenn noch nicht applicable, z.B. Mindestbetrag nicht erreicht)
   * 4. Standard-Kampagne (is_standard_campaign === true)
   *
   * @returns {Object|null} Die aktive Kampagne oder null bei Fehler
   */
  function determineActiveCampaign() {
    const cart = window.liquidAjaxCart.cart;
    const discountCodes = cart.discount_codes || [];
    const discountApplications = cart.cart_level_discount_applications || [];
    const activeCodeAttr = cart.attributes?.active_code;

    try {
      // Standard-Kampagne suchen (Fallback)
      const standardCampaign = existingCampaigns.find(camp => camp.is_standard_campaign === true);

      if (discountCodes.length === 0) {
        return standardCampaign;
      }
      // 0. Kampagne anhand des aktiven Codes im Warenkorb-Attribut suchen (höchste Priorität)
      const campaignWithActiveAttr = activeCodeAttr && discountCodes.includes(activeCodeAttr)
        ? existingCampaigns.find(camp => camp.code?.toLowerCase() === activeCodeAttr.toLowerCase())
        : null;

      // 1. Kampagne mit anwendbarem (applicable) Code suchen
      const applicableCode = discountCodes.find(code => code.applicable === true)?.code;
      const campaignWithApplicableCode = !campaignWithActiveAttr && applicableCode
        ? existingCampaigns.find(camp => camp.code === applicableCode)
        : null;

      // 2. Falls kein applicable Code gefunden: prüfen ob ein Cart-Code zu einer Kampagne passt
      //    (z.B. Mindestbestellwert noch nicht erreicht).
      //    WICHTIG: Damit eine Kampagne als aktiv gilt, muss ihr Code zwingend im Warenkorb
      //    (in discount_codes oder cart_level_discount_applications) auftauchen!
      const campaignWithAnyCode = !campaignWithActiveAttr && !campaignWithApplicableCode
        ? existingCampaigns.find(camp => {
          if (!camp.code) return false;
          const campCodeLower = camp.code.toLowerCase();

          const inDiscountCodes = discountCodes.some(dc => dc.code?.toLowerCase() === campCodeLower);
          const inApplications = discountApplications.some(da => da.title?.toLowerCase() === campCodeLower);

          // Ist der Code der Kampagne im Warenkorb als Rabatt/Applikation vorhanden?
          const isCodeInCart = inDiscountCodes || inApplications;

          // Der Code muss zwingend im Warenkorb zu finden sein.
          return isCodeInCart;
        })
        : null;

      // Priorität: active_code Attribut > applicable Code > beliebiger passender Code > Standard-Kampagne
      const activeCampaign = campaignWithActiveAttr || campaignWithApplicableCode || campaignWithAnyCode || standardCampaign;

      return activeCampaign;
    } catch (error) {
      console.error('Fehler beim Bestimmen der aktiven Kampagne:', error);
      return null;
    }
  }

  /**
   * Speichert die aktive Kampagne im Local Storage
   *
   * @param {Object} activeCampaign - Die zu speichernde Kampagne
   * @param {string} activeCampaign.title - Titel der Kampagne
   * @param {string} activeCampaign.code - Discount-Code der Kampagne
   * @param {boolean} activeCampaign.is_standard - Ob es die Standard-Kampagne ist
   * @param {Array} activeCampaign.steps - Kampagnen-Schritte mit Geschenken
   * @param {Array} activeCampaign.shipping_tariffs - Versandtarife der Kampagne
   * @param {string} requestCountry - ISO-Code des Landes (z.B. "DE", "AT")
   */
  function setStorageCampaign(activeCampaign, requestCountry) {
    // Kampagnen-Daten mit Zeitstempel im Local Storage speichern
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      title: activeCampaign.title,
      code: activeCampaign.code,
      is_standard: activeCampaign.is_standard,
      steps: activeCampaign.steps,
      shipping_tariffs: activeCampaign.shipping_tariffs,
      request_country: requestCountry,
      updated_at: new Date().toISOString()
    }));
  }

  /**
   * Ruft die aktive Kampagne aus dem Local Storage ab
   *
   * @returns {Object|null} Die gespeicherte Kampagne oder null, falls keine vorhanden
   */
  function getActiveCampaign() {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  }

  /**
   * Liest die vom Nutzer manuell entfernten Geschenk-Varianten aus dem Cart-Attribut
   * `_dismissed_gifts`. Wird sowohl in der Mutation als auch im Click-Handler genutzt.
   *
   * @returns {number[]} Array mit Variant-IDs, die der Nutzer explizit entfernt hat
   */
  function getDismissedGiftIds() {
    const cart = window.liquidAjaxCart?.cart;
    const raw = cart?.attributes?._dismissed_gifts;
    if (!raw) return [];
    return String(raw).split(',').map(s => parseInt(s.trim(), 10)).filter(Boolean);
  }

  /**
   * Berechnet und speichert den Versandstatus basierend auf Warenkorb-Wert und Versandtarifen
   *
   * @param {number} cartTotal - Aktueller Warenkorb-Wert
   * @param {string} requestCountry - ISO-Code des Landes
   * @param {Array} shippingTariffs - Array mit Versandtarifen
   */
  function setShippingStatus(cartTotal, requestCountry, shippingTariffs) {
    if (!shippingTariffs || shippingTariffs.length === 0) {
      localStorage.removeItem('ds_shipping_status');
      return;
    }

    // Wenn keine physischen Items vorhanden sind, wird nichts versendet —
    // Shipping-Status entfernen, damit kein veralteter Eintrag stehen bleibt.
    const cart = window.liquidAjaxCart?.cart;
    const cartNeedsShipping = cart?.items?.some(item => item.requires_shipping);
    if (cart && !cartNeedsShipping) {
      localStorage.removeItem('ds_shipping_status');
      return;
    }

    // Passenden Tarif für das Land finden
    const tariff = shippingTariffs.find(t => t.country_code === requestCountry);

    if (!tariff) {
      localStorage.removeItem('ds_shipping_status');
      return;
    }

    // Versandstatus berechnen
    const isFreeShipping = cartTotal >= tariff.free_from;
    const shippingCost = isFreeShipping ? 0 : tariff.standard_cost;
    const remainingForFree = Math.max(0, tariff.free_from - cartTotal);

    // Status im Local Storage speichern
    localStorage.setItem('ds_shipping_status', JSON.stringify({
      country_code: requestCountry,
      is_free: isFreeShipping,
      cost: shippingCost,
      free_from: tariff.free_from,
      remaining: remainingForFree,
      cart_total: cartTotal,
      updated_at: new Date().toISOString()
    }));
  }


  // ============================================================================
  // MUTATION FUNKTIONEN - WARENKORB AKTUALISIERUNG
  // ============================================================================

  /**
   * Mutation: Lädt und speichert die aktive Kampagne
   *
   * Diese Mutation wird bei jeder Warenkorb-Änderung ausgeführt und stellt sicher,
   * dass immer die korrekte Kampagne im Local Storage gespeichert ist.
   *
   * @returns {void}
   */
  function campaignLoaderMutation() {
    // Aktive Kampagne bestimmen
    const activeCampaign = determineActiveCampaign();

    // Kampagne im Local Storage speichern, falls vorhanden
    if (activeCampaign) {
      // Request Country und Cart Total aus dem JSON-Element lesen
      const requestCountry = campaignsJson.request_country;
      const cartTotal = campaignsJson.cart_total;

      // Kampagne mit Request Country speichern
      setStorageCampaign(activeCampaign, requestCountry);

      // Versandstatus berechnen und speichern
      setShippingStatus(cartTotal, requestCountry, activeCampaign.shipping_tariffs);
    }
  }

  /**
   * Mutation: Verwaltet automatische Geschenke im Warenkorb
   *
   * Diese Mutation:
   * 1. Prüft, welche Kampagnen-Schritte basierend auf dem Warenkorbwert erreicht wurden
   * 2. Entfernt Geschenke, die nicht mehr berechtigt sind
   * 3. Fügt neue berechtigte Geschenke hinzu
   * 4. Aktualisiert die Menge vorhandener Geschenke
   *
   * @returns {Object|undefined} Objekt mit requests-Array für Warenkorb-Änderungen,
   *                             oder undefined, falls keine Änderungen nötig sind
   */
  function freeItemsMutation() {
    // Aktive Kampagne aus Local Storage abrufen
    const activeCampaign = getActiveCampaign();
    if (!activeCampaign) return;

    // Warenkorb-Daten abrufen
    const cart = window.liquidAjaxCart.cart;
    if (!cart) return;

    // Warenkorbwert in Hauptwährungseinheit umrechnen (Cent -> Euro)
    // Virtuelle Produkte (Gutscheine, Tickets — requires_shipping: false) dürfen
    // Gratis-Geschenk-Schwellen nicht auslösen.
    const cartTotal = cart.items
      .filter(item => item.requires_shipping)
      .reduce((sum, item) => sum + item.final_line_price, 0) / 100;

    // Array für alle Warenkorb-Änderungsanfragen
    const requests = [];

    // CH-Ausschluss: Sendungen in die Schweiz dürfen aus zollrechtlichen Gründen
    // keine Positionen mit deklariertem Warenwert 0 enthalten. Ein leeres
    // eligibleSteps-Array sorgt unten dafür, dass alle vorhandenen Gifts entfernt
    // und keine neuen hinzugefügt werden.
    const shippingCountry =
      cart.shipping_address?.country_code
      || activeCampaign.request_country;
    const isExcludedCountry = shippingCountry === 'CH';

    // -------------------------------------------------------------------------
    // SCHRITT 1: Berechtigte Kampagnen-Schritte ermitteln
    // -------------------------------------------------------------------------

    /**
     * Filtert alle Schritte, die:
     * - Vom Typ 'automatic_gift' oder 'selectable_gifts' sind
     * - Deren Mindestbetrag (free_from_amount) erreicht wurde
     */
    const eligibleSteps = isExcludedCountry ? [] : activeCampaign.steps.filter(step => {
      return (step.type === 'automatic_gift' || step.type === 'selectable_gifts')
        && cartTotal >= step.free_from_amount;
    });

    // -------------------------------------------------------------------------
    // DISMISSED GIFTS: vom Nutzer manuell entfernte Gratis-Artikel
    // -------------------------------------------------------------------------
    // Ohne diese Liste würde das Geschenk nach jedem Remove sofort wieder
    // nachgelegt werden, solange die Mindestbestellschwelle erreicht ist —
    // der User kann das Item also faktisch nicht löschen.
    //
    // Cleanup: Varianten, deren Step nicht mehr berechtigt ist (Warenkorb unter
    // Schwelle, Step entfernt o.ä.), werden aus der Liste geworfen, damit der
    // Nutzer das Geschenk beim nächsten Mal wieder bekommen kann.
    const dismissedGiftIds = getDismissedGiftIds();
    const eligibleGiftVariantSet = new Set();
    eligibleSteps.forEach(step => {
      if (step.gift_products) {
        step.gift_products.forEach(gift => {
          if (gift.variant_id) eligibleGiftVariantSet.add(gift.variant_id);
        });
      }
    });
    const cleanedDismissed = dismissedGiftIds.filter(id => eligibleGiftVariantSet.has(id));
    const dismissedChanged = cleanedDismissed.length !== dismissedGiftIds.length;

    // -------------------------------------------------------------------------
    // SCHRITT 2: Alle gültigen Geschenk-Varianten-IDs sammeln
    // -------------------------------------------------------------------------

    /**
     * Sammelt alle Varianten-IDs von Geschenken aus berechtigten Schritten
     * Diese Liste wird verwendet, um nicht mehr berechtigte Geschenke zu entfernen
     */
    let validGiftVariantIds = [];
    eligibleSteps.forEach(step => {
      if (step.gift_products) {
        step.gift_products.forEach(product => {
          if (product.variant_id) {
            validGiftVariantIds.push(product.variant_id);
          }
        });
      }
    });

    // -------------------------------------------------------------------------
    // SCHRITT 3: Nicht mehr berechtigte Geschenke entfernen & Mengen anpassen
    // -------------------------------------------------------------------------

    /**
     * Objekt für /cart/update.js Request
     * Format: { [line_item_key]: quantity }
     */
    const updates = {};

    /**
     * Nur Step-IDs aus berechtigten Schritten (Mindestbetrag erreicht) sind gültig
     */
    const validStepIds = eligibleSteps.map(step => String(step.id));

    /**
     * Alle Geschenk-Varianten-IDs der aktiven Kampagne (alle Schritte, nicht nur berechtigte)
     */
    const activeCampaignAllGiftIds = new Set();
    activeCampaign.steps.forEach(step => {
      if (step.gift_products) {
        step.gift_products.forEach(gift => {
          if (gift.variant_id) activeCampaignAllGiftIds.add(gift.variant_id);
        });
      }
    });

    /**
     * Geschenk-Varianten-IDs aus ANDEREN Kampagnen sammeln (die nicht in der aktiven vorkommen)
     */
    const otherCampaignGiftIds = new Set();
    existingCampaigns.forEach(camp => {
      if (camp.code === activeCampaign.code) return;
      if (camp.steps) {
        camp.steps.forEach(step => {
          if (step.gift_products) {
            step.gift_products.forEach(gift => {
              if (gift.variant_id && !activeCampaignAllGiftIds.has(gift.variant_id)) {
                otherCampaignGiftIds.add(gift.variant_id);
              }
            });
          }
        });
      }
    });

    /**
     * Entferne Items mit _custom_gift_step, die nicht mehr zur aktiven Kampagne gehören —
     * entweder weil die Step-ID ungültig ist, oder weil die Variante einer anderen Kampagne gehört
     */
    cart.items.forEach(item => {
      if (item.properties && item.properties._custom_gift_step) {
        const itemStepId = String(item.properties._custom_gift_step);
        if (!validStepIds.includes(itemStepId) || otherCampaignGiftIds.has(item.variant_id)) {
          updates[item.key] = 0;
        }
      }
    });

    /**
     * Für jeden berechtigten Schritt: Custom Gifts validieren
     */
    eligibleSteps.forEach(step => {
      const stepId = String(step.id);
      const giftSelectionLimit = step.gift_selection_limit || 1;

      // Finde alle Custom Gifts für diesen Schritt
      const customGiftsForStep = cart.items.filter(item =>
        item.properties &&
        item.properties._custom_gift_step === stepId
      );

      // Wenn mehr Custom Gifts als erlaubt: Überschüssige auf 0 setzen
      if (customGiftsForStep.length > giftSelectionLimit) {
        for (let i = 0; i < customGiftsForStep.length; i++) {
          // Erste N Gifts behalten (quantity = 1), Rest entfernen (quantity = 0)
          updates[customGiftsForStep[i].key] = (i < giftSelectionLimit ? 1 : 0);
        }
      }
    });

    // Update-Request erstellen, falls Artikel-Entfernungen oder Attribut-Änderungen vorliegen
    const attributes = {};
    if ((!cart.discount_codes || cart.discount_codes.length === 0) && cart.attributes?.active_code) {
      attributes.active_code = '';
      console.log('remove active code');
    }

    if (dismissedChanged) {
      attributes._dismissed_gifts = cleanedDismissed.length > 0 ? cleanedDismissed.join(',') : '';
    }

    if (Object.keys(updates).length > 0 || Object.keys(attributes).length > 0) {
      const updateBody = { updates };
      if (Object.keys(attributes).length > 0) {
        updateBody.attributes = attributes;
      }

      requests.push({
        type: 'update',
        body: updateBody
      });
    }

    // -------------------------------------------------------------------------
    // SCHRITT 4: Neue automatische Geschenke hinzufügen
    // -------------------------------------------------------------------------

    /**
     * Array für neue Artikel, die dem Warenkorb hinzugefügt werden sollen
     * Wird am Ende als ein einziger 'add'-Request gesendet
     */
    const itemsToAdd = [];

    eligibleSteps.forEach(step => {
      // Nur automatische Geschenke verarbeiten (selectable_gifts werden separat behandelt)
      if (step.type === 'automatic_gift' && step.gift_products) {
        step.gift_products.forEach(gift => {
          const variantId = gift.variant_id;

          // Vom Nutzer entfernte Geschenke nicht erneut hinzufügen
          if (cleanedDismissed.includes(variantId)) return;

          const stepId = step.id;

          const stepAmount = gift.amount || 1; // Standardmenge ist 1

          // Prüfen, ob Artikel bereits im Warenkorb vorhanden ist
          const existingItem = cart.items.find(item =>
            item.variant_id === variantId
            // && item.line_price <= 0  // Optional: Nur kostenlose Artikel berücksichtigen
          );

          if (existingItem) {
            // ---------------------------------------------------------------
            // Artikel existiert bereits: Menge aktualisieren falls nötig
            // ---------------------------------------------------------------

            // Sicherstellen, dass die Menge mindestens der Schritt-Menge entspricht
            if (existingItem.quantity < stepAmount) {
              requests.push({
                type: 'change',
                body: {
                  id: existingItem.key,  // Wichtig: key verwenden, nicht variant_id
                  quantity: stepAmount
                }
              });
            }
          } else {
            // ---------------------------------------------------------------
            // Artikel existiert nicht: Zum Hinzufügen vormerken
            // ---------------------------------------------------------------

            itemsToAdd.push({
              id: variantId,
              quantity: stepAmount,
              properties: {
                _custom_gift_step: String(stepId)     // Zuordnung zum Kampagnen-Schritt
              }
            });
          }
        });
      }
    });

    // Alle neuen Artikel in einem Request hinzufügen (effizienter als einzelne Requests)
    if (itemsToAdd.length > 0) {
      requests.push({
        type: 'add',
        body: {
          items: itemsToAdd
        }
      });
    }

    // -------------------------------------------------------------------------
    // RÜCKGABE: Requests an Liquid Ajax Cart zurückgeben
    // -------------------------------------------------------------------------

    /**
     * Wenn Änderungen vorliegen, werden diese als requests-Array zurückgegeben
     * Liquid Ajax Cart führt diese dann aus
     */
    if (requests.length > 0) {
      return {
        requests: requests
      };
    }
  }


  // ============================================================================
  // LIQUID AJAX CART KONFIGURATION
  // ============================================================================

  /**
   * Registriert die Mutation-Funktionen bei Liquid Ajax Cart
   * Diese werden bei jeder Warenkorb-Änderung in der angegebenen Reihenfolge ausgeführt
   */
  window.liquidAjaxCart.conf('mutations', [
    campaignLoaderMutation,  // Zuerst: Kampagne laden und speichern
    freeItemsMutation        // Dann: Geschenke verwalten
  ]);

  /**
   * Deaktiviert automatische Warenkorb-Updates beim Fokussieren des Fensters
   * Verhindert unnötige Server-Anfragen
   */
  window.liquidAjaxCart.conf("updateOnWindowFocus", false);


  // ============================================================================
  // GIFT REMOVAL: Klick-Interceptor
  // ============================================================================

  /**
   * Fängt Klicks auf den Entfernen-Button von Geschenk-Items ab, bevor LAC seinen
   * Standard-Remove-Request feuert. Wir senden EINEN kombinierten /cart/update.js-
   * Aufruf, der das Item entfernt UND die Variant-ID in `_dismissed_gifts` schreibt.
   *
   * Warum atomar? Würden wir erst entfernen und danach das Attribut setzen, läuft
   * `freeItemsMutation` zwischendrin auf dem Zustand ohne Dismiss-Flag und legt
   * das Geschenk sofort wieder nach.
   *
   * Capture-Phase, damit dieser Handler vor dem Bubble-Listener von LAC läuft.
   */
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-ajax-cart-request-button]');
    if (!btn) return;
    if (!btn.closest('cart-remove-button')) return;

    const cart = window.liquidAjaxCart?.cart;
    if (!cart || !cart.items) return;

    // Item-Key aus URL parsen — robuster als variant_id, falls eine Variante
    // mehrfach (mit unterschiedlichen properties) im Warenkorb liegt.
    const urlAttr = btn.getAttribute('data-ajax-cart-request-button') || '';
    const idMatch = urlAttr.match(/[?&]id=([^&]+)/);
    if (!idMatch) return;
    const itemKey = decodeURIComponent(idMatch[1]);

    const item = cart.items.find(i => i.key === itemKey);
    if (!item) return;

    // Nur Gift-Items abfangen (mit _custom_gift_step property)
    if (!item.properties?._custom_gift_step) return;

    event.preventDefault();
    event.stopPropagation();

    const dismissed = getDismissedGiftIds();
    if (!dismissed.includes(item.variant_id)) {
      dismissed.push(item.variant_id);
    }

    window.liquidAjaxCart.update({
      updates: { [item.key]: 0 },
      attributes: { _dismissed_gifts: dismissed.join(',') }
    });
  }, true);

})();